import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  ClockCounterClockwise, MagnifyingGlass, ArrowsClockwise, Download,
  Package, MapPin, Lightning, Truck, FileText, Bell, Envelope,
  DeviceMobile, ShieldCheck, Warning, CheckCircle, ChatCircle,
  CloudArrowUp, Thermometer, Drop, Sun, ArrowsLeftRight, User,
} from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SEV = {
  critical: { bg: "bg-red-500/15",    text: "text-red-300",    dot: "bg-red-500",    label: "CRITICAL" },
  warning:  { bg: "bg-amber-500/15",  text: "text-amber-300",  dot: "bg-amber-400",  label: "WARNING" },
  info:     { bg: "bg-sky-500/15",    text: "text-sky-300",    dot: "bg-sky-400",    label: "INFO" },
};

// Visual mapping per event_type — chooses an icon and accent color for the
// timeline dot so the eye picks the story up fast.
const EVENT_VISUAL = {
  "shipment.created":              { icon: Package,         color: "text-teal-400" },
  "shipment.edited":               { icon: Package,         color: "text-zinc-300" },
  "shipment.started":              { icon: Truck,           color: "text-sky-400" },
  "shipment.delivered":            { icon: CheckCircle,     color: "text-emerald-400" },
  "shipment.status_changed":       { icon: ArrowsLeftRight, color: "text-zinc-300" },
  "shipment.assigned":             { icon: User,            color: "text-zinc-300" },
  "shipment.signed":               { icon: ShieldCheck,     color: "text-emerald-400" },
  "shipment.quarantined":          { icon: Warning,         color: "text-red-400" },
  "shipment.released":             { icon: CheckCircle,     color: "text-emerald-400" },
  "logger.uploaded":               { icon: CloudArrowUp,    color: "text-teal-400" },
  "logger.attached":               { icon: ShieldCheck,     color: "text-teal-400" },
  "logger.replay_executed":        { icon: Lightning,       color: "text-teal-400" },
  "logger.discarded":              { icon: CloudArrowUp,    color: "text-zinc-500" },
  "logger.shipment_created":       { icon: Package,         color: "text-teal-400" },
  "facility.arrival":              { icon: MapPin,          color: "text-emerald-400" },
  "facility.departure":            { icon: MapPin,          color: "text-amber-400" },
  "excursion.temperature":         { icon: Thermometer,     color: "text-red-400" },
  "excursion.humidity":            { icon: Drop,            color: "text-amber-400" },
  "excursion.light":               { icon: Sun,             color: "text-amber-400" },
  "alert.created":                 { icon: Warning,         color: "text-amber-400" },
  "alert.acknowledged":            { icon: CheckCircle,     color: "text-sky-300" },
  "alert.escalated":               { icon: Lightning,       color: "text-red-400" },
  "alert.resolved":                { icon: CheckCircle,     color: "text-emerald-400" },
  "alert.dismissed":               { icon: CheckCircle,     color: "text-zinc-500" },
  "corrective_action.created":     { icon: FileText,        color: "text-amber-300" },
  "corrective_action.acknowledged":{ icon: CheckCircle,     color: "text-sky-300" },
  "corrective_action.completed":   { icon: CheckCircle,     color: "text-emerald-400" },
  "corrective_action.escalated":   { icon: Lightning,       color: "text-red-400" },
  "corrective_action.overdue":     { icon: Warning,         color: "text-amber-400" },
  "notification.sent":             { icon: Envelope,        color: "text-emerald-400" },
  "notification.mocked":           { icon: Envelope,        color: "text-zinc-500" },
  "notification.failed":           { icon: Envelope,        color: "text-red-400" },
  "notification.retried":          { icon: Envelope,        color: "text-amber-300" },
  "user.comment":                  { icon: ChatCircle,      color: "text-violet-300" },
  "user.acknowledgement":          { icon: CheckCircle,     color: "text-sky-300" },
};

function visualFor(eventType) {
  return EVENT_VISUAL[eventType]
      || EVENT_VISUAL[eventType?.replace("legacy.", "")]
      || { icon: FileText, color: "text-zinc-400" };
}

function relativeTime(iso) {
  if (!iso) return "—";
  const dt = new Date(iso);
  const diff = (Date.now() - dt.getTime()) / 1000;
  if (diff < 60)    return `${Math.round(diff)}s ago`;
  if (diff < 3600)  return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  return dt.toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

/**
 * AuditTrailView — global, filterable, append-only investigation log.
 *
 * Top-level Audit nav surface. Combines:
 *   - filter strip (event_type / severity / shipment / actor / q)
 *   - one-tap CSV export of current filter
 *   - timeline rendering (dot + icon + summary + meta)
 *   - cross-link: clicking a shipment-id chip jumps to that shipment detail
 *
 * For per-shipment timeline embedded in ShipmentDetail, see ShipmentAuditTimeline.
 */
export default function AuditTrailView({ onSelectShipment }) {
  const [enums, setEnums] = useState({ event_types: [], severities: [] });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [eventType, setEventType] = useState("__all");
  const [severity, setSeverity] = useState("__all");
  const [shipmentId, setShipmentId] = useState("");

  useEffect(() => {
    axios.get(`${API}/audit/meta/enums`)
      .then((r) => setEnums(r.data))
      .catch(() => {});
  }, []);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (q) params.q = q;
      if (eventType && eventType !== "__all") params.event_type = eventType;
      if (severity && severity !== "__all") params.severity = severity;
      if (shipmentId) params.shipment_id = shipmentId;
      const r = await axios.get(`${API}/audit`, { params });
      setItems(r.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load audit trail");
    } finally {
      setLoading(false);
    }
  }, [q, eventType, severity, shipmentId]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const grouped = useMemo(() => {
    const out = [];
    let last = null;
    items.forEach((e) => {
      const day = (e.timestamp || "").slice(0, 10);
      if (day !== last) {
        out.push({ kind: "day", day });
        last = day;
      }
      out.push({ kind: "event", event: e });
    });
    return out;
  }, [items]);

  const exportCsv = async () => {
    const params = {};
    if (q) params.q = q;
    if (eventType && eventType !== "__all") params.event_type = eventType;
    if (severity && severity !== "__all") params.severity = severity;
    if (shipmentId) params.shipment_id = shipmentId;
    try {
      const r = await axios.get(`${API}/audit/export.csv`, {
        params,
        responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `holotrak-audit-${Date.now()}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Audit trail exported");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "CSV export failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="audit-trail-view">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <h2 className="text-xl font-semibold text-zinc-50 flex items-center gap-2">
            <ClockCounterClockwise size={20} weight="duotone" className="text-violet-400" />
            Audit Trail
          </h2>
          <Badge variant="outline" className="border-zinc-700 text-xs font-mono">
            {items.length}
          </Badge>
          <span className="text-[10px] uppercase tracking-wider text-zinc-500">
            Append-only · SHA-256 fingerprinted · CFR 21 Part 11 ready
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm"
            onClick={exportCsv}
            className="border-zinc-700 hover:bg-zinc-800 text-xs h-8"
            data-testid="audit-export-csv">
            <Download size={12} className="mr-1" /> Export CSV
          </Button>
          <Button variant="outline" size="sm"
            onClick={fetchItems}
            className="border-zinc-700 hover:bg-zinc-800 text-xs h-8"
            data-testid="audit-refresh">
            <ArrowsClockwise size={12} className={loading ? "animate-spin mr-1" : "mr-1"} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="bg-zinc-900 border-zinc-800 p-3 flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <MagnifyingGlass size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search summary, actor, type…"
            className="bg-zinc-950 border-zinc-800 text-zinc-200 h-9 pl-8 text-xs"
            data-testid="audit-search" />
        </div>
        <Select value={eventType} onValueChange={setEventType}>
          <SelectTrigger className="w-56 bg-zinc-950 border-zinc-800 text-zinc-200 h-9 text-xs"
            data-testid="audit-filter-event-type">
            <SelectValue placeholder="Any event" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-200 max-h-[400px]">
            <SelectItem value="__all">Any event type</SelectItem>
            {enums.event_types.map((et) => (
              <SelectItem key={et} value={et}>{et}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={severity} onValueChange={setSeverity}>
          <SelectTrigger className="w-36 bg-zinc-950 border-zinc-800 text-zinc-200 h-9 text-xs"
            data-testid="audit-filter-severity">
            <SelectValue placeholder="Any severity" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-200">
            <SelectItem value="__all">Any severity</SelectItem>
            {enums.severities.map((s) => (
              <SelectItem key={s} value={s}>{s}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input value={shipmentId} onChange={(e) => setShipmentId(e.target.value)}
          placeholder="Shipment ID"
          className="w-44 bg-zinc-950 border-zinc-800 text-zinc-200 h-9 text-xs font-mono"
          data-testid="audit-filter-shipment" />
      </Card>

      {/* Timeline */}
      {loading && items.length === 0 ? (
        <p className="text-xs text-zinc-500 text-center py-12 animate-pulse">Loading audit trail…</p>
      ) : items.length === 0 ? (
        <Card className="bg-zinc-900 border-zinc-800 p-12 text-center" data-testid="audit-empty">
          <ClockCounterClockwise size={36} weight="duotone" className="text-zinc-700 mx-auto mb-3" />
          <p className="text-sm text-zinc-400 mb-1">No audit events match your filter</p>
          <p className="text-xs text-zinc-600">
            Logger uploads, shipment lifecycle changes, excursions, corrective actions and
            notification dispatch attempts will all appear here.
          </p>
        </Card>
      ) : (
        <div className="space-y-1" data-testid="audit-timeline">
          {grouped.map((entry, i) =>
            entry.kind === "day" ? (
              <div key={`day-${entry.day}-${i}`}
                className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono py-2 px-1 sticky top-0 bg-zinc-950/80 backdrop-blur z-10">
                {new Date(entry.day).toLocaleDateString([], {
                  weekday: "short", month: "long", day: "numeric", year: "numeric",
                })}
              </div>
            ) : (
              <AuditRow key={entry.event.id} event={entry.event}
                onSelectShipment={onSelectShipment} />
            )
          )}
        </div>
      )}
    </div>
  );
}

export function AuditRow({ event: e, onSelectShipment, compact = false }) {
  const v = visualFor(e.event_type);
  const Icon = v.icon;
  const sev = SEV[e.severity] || SEV.info;

  return (
    <div className="flex items-start gap-3 px-3 py-2.5 hover:bg-zinc-900/50 rounded-sm border border-transparent hover:border-zinc-800 transition-colors"
      data-testid={`audit-row-${e.id}`}>
      <div className={`mt-1 w-7 h-7 rounded-full bg-zinc-900 border border-zinc-800 flex items-center justify-center flex-shrink-0 ${v.color}`}>
        <Icon size={14} weight="duotone" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-zinc-200 font-medium">{e.summary || e.event_type}</span>
          <Badge className={`text-[9px] uppercase tracking-wider font-mono ${sev.bg} ${sev.text} border-0`}>
            {sev.label}
          </Badge>
          <span className="text-[10px] font-mono text-zinc-600">{e.event_type}</span>
          {e.is_legacy && (
            <Badge className="text-[8px] bg-zinc-800 text-zinc-500 border-0">LEGACY</Badge>
          )}
        </div>
        <div className="flex items-center gap-3 mt-1 flex-wrap text-[10px] font-mono text-zinc-500">
          <span><User size={9} className="inline mr-0.5" />{e.actor_email}</span>
          <span>{relativeTime(e.timestamp)}</span>
          {e.shipment_id && (
            <button onClick={() => onSelectShipment?.(e.shipment_id)}
              className="text-teal-400 hover:underline truncate max-w-[140px]"
              title={e.shipment_id}
              data-testid={`audit-shipment-link-${e.shipment_id}`}>
              <Package size={9} className="inline mr-0.5" />ship/{e.shipment_id.slice(0, 8)}
            </button>
          )}
          {e.logger_import_id && (
            <span title={e.logger_import_id}>
              <CloudArrowUp size={9} className="inline mr-0.5" />logger/{e.logger_import_id.slice(0, 8)}
            </span>
          )}
          {e.facility_id && (
            <span title={e.facility_id}>
              <MapPin size={9} className="inline mr-0.5" />facility/{e.facility_id.slice(0, 8)}
            </span>
          )}
          {e.device_name && (
            <span className="text-zinc-400 truncate max-w-[160px]">device: {e.device_name}</span>
          )}
        </div>
        {!compact && e.details && Object.keys(e.details).length > 0 && (
          <details className="mt-1.5">
            <summary className="text-[10px] font-mono text-zinc-600 cursor-pointer hover:text-zinc-400">
              details
            </summary>
            <pre className="text-[10px] font-mono text-zinc-500 bg-zinc-950 border border-zinc-900 rounded-sm p-2 mt-1 overflow-x-auto max-w-full whitespace-pre-wrap break-all">
              {JSON.stringify(e.details, null, 2)}
            </pre>
          </details>
        )}
      </div>
      {!compact && e.checksum && (
        <span className="text-[9px] font-mono text-zinc-700 mt-1.5" title={`SHA-256: ${e.checksum}`}>
          {e.checksum.slice(0, 8)}
        </span>
      )}
    </div>
  );
}
